import { PspDrawer } from "@/components/pspDrawer/pspDrawer";
import { translate } from "@/config/translator";
// eslint-disable-next-line max-len
import { useAttendanceDesktopVersionsDrawer } from "./attendanceDesktopVersionsDrawer/useAttendanceDesktopVersionsDrawer";
import { Version } from "@/api/openapi";
import { clsx } from "clsx/lite";
import Skeleton from "react-loading-skeleton";
import TextButton from "@/components/textButton/TextButton";
import { formatDateFromLang, formatDateFromLangLong } from "@/config/utils";
import ConfirmationDialog from "@/components/confirmationDialog/ConfirmationDialog";
import { Dispatch, SetStateAction } from "react";
import { TempAttendanceMonth } from "@/config/types";

type AttendanceDesktopVersionsDrawerProps = {
  isOpen: boolean;
  setTempAttendance: Dispatch<SetStateAction<TempAttendanceMonth | undefined>>;
  onClose: () => void;
};

export function AttendanceDesktopVersionsDrawer({
  isOpen,
  setTempAttendance,
  onClose,
}: AttendanceDesktopVersionsDrawerProps) {
  const {
    lang,
    attendanceVersions,
    setAttendanceMonth,
    closeRestoreVersionConfirmation,
    openRestoreVersionConfirmation,
    restoreConfirmationOpen,
    restoreConfirmationOpenVersion,
    onRestoreVersion,
  } = useAttendanceDesktopVersionsDrawer({ onClose, setTempAttendance });

  return (
    <PspDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={translate("pastVersions", lang)}
    >
      <div className="flex flex-col gap-4">
        {(attendanceVersions ?? Array.from({ length: 10 })).map(
          (version: Version | undefined, index) => {
            const versionDate = version
              ? new Date(version.timestamp)
              : undefined;

            return (
              <div
                key={`past-version-desktop-item-${version?.id ?? index}`}
                className={clsx(
                  "bg-canvas-secondary",
                  "flex",
                  "flex-row",
                  "rounded-lg",
                  "justify-between",
                  "p-4",
                  "font-semibold",
                )}
              >
                <div className="flex flex-row gap-3 items-center">
                  {versionDate && version ? (
                    `${formatDateFromLangLong(
                      versionDate,
                      lang,
                    )} - ${versionDate
                      .getHours()
                      .toString()
                      .padStart(2, "0")}:${versionDate
                      .getMinutes()
                      .toString()
                      .padStart(2, "0")}`
                  ) : (
                    <>
                      <Skeleton width={100} /> - <Skeleton width={50} />
                    </>
                  )}
                  {version?.draft && (
                    <div className="px-2 py-1 text-xs bg-primary-50 rounded-sm text-primary">
                      {translate("draft", lang)}
                    </div>
                  )}
                </div>
                <TextButton
                  onClick={() =>
                    version ? openRestoreVersionConfirmation(version) : {}
                  }
                >
                  {translate("restore", lang)}
                </TextButton>
              </div>
            );
          },
        )}
      </div>
      <ConfirmationDialog
        isOpen={restoreConfirmationOpen}
        onCancel={closeRestoreVersionConfirmation}
        onConfirm={onRestoreVersion}
        messageString={translate("youAreAboutToRestoreVersion", lang, {
          "{date}": restoreConfirmationOpenVersion
            ? formatDateFromLang(
                new Date(restoreConfirmationOpenVersion.timestamp),
                lang,
              )
            : "",
        })}
        titleString={translate("areYouSure", lang)}
      />
    </PspDrawer>
  );
}
